import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useAppMode } from "@/context/AppModeContext";
import { toast } from "sonner";
import { Facebook, Twitter, Send, MessageCircle, Copy } from "lucide-react";

const SECTION_META = {
  fire: { emblem: "🔥", label: "Most In-Demand on DLuxe", brag: "I'm one of the most in-demand members on DLuxe" },
  diamond: { emblem: "💎", label: "Diamond Member", brag: "I'm a Diamond member — premium dates only" },
  top_rated: { emblem: "⭐", label: "Top Rated Dater", brag: "I'm one of the top-rated daters" },
};

export default function BadgeShareDialog({ open, onOpenChange, member, section }) {
  const { appName } = useAppMode();
  const [custom, setCustom] = useState("");

  useEffect(() => {
    if (open) setCustom("");
  }, [open]);

  if (!member) return null;
  const meta = SECTION_META[section] || SECTION_META.top_rated;
  const link = window.location.origin;
  const defaultMsg = `${meta.emblem} ${meta.brag} on ${appName}! Think you can compete?`;
  const message = custom.trim() || defaultMsg;
  const text = `${message} ${link}`;
  const enc = encodeURIComponent(text);

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* noop */
    }
    toast.success("Brag copied — paste it anywhere!");
  };

  const tiles = [
    { name: "WhatsApp", icon: MessageCircle, color: "text-green-400", href: `https://wa.me/?text=${enc}` },
    { name: "Facebook", icon: Facebook, color: "text-blue-400", href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}&quote=${enc}` },
    { name: "X", icon: Twitter, color: "text-sky-400", href: `https://twitter.com/intent/tweet?text=${enc}` },
    { name: "Telegram", icon: Send, color: "text-cyan-400", href: `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(message)}` },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm bg-card border-white/10" data-testid="badge-share-dialog">
        <DialogHeader>
          <DialogTitle>Share your badge</DialogTitle>
        </DialogHeader>

        <div
          className="rounded-2xl p-8 text-center bg-gradient-to-br from-primary/30 via-background to-amber-500/20 border border-white/15"
          data-testid="badge-share-card"
        >
          <p className="text-6xl mb-3">{meta.emblem}</p>
          <p className="text-xl font-bold font-['Outfit']">{member.name}</p>
          <p className="text-sm text-amber-300 font-medium mt-1">{meta.label}</p>
          <p className="text-[11px] text-muted-foreground mt-4 tracking-widest uppercase">{appName} · Top Members</p>
        </div>

        <div>
          <p className="text-xs text-muted-foreground mb-1.5">Customize your message (your link is added automatically)</p>
          <Textarea
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            placeholder={defaultMsg}
            rows={2}
            maxLength={280}
            className="bg-white/5 border-white/10 text-sm"
            data-testid="badge-share-message-input"
          />
        </div>

        <div className="grid grid-cols-5 gap-2 mt-2">
          {tiles.map((t) => (
            <a
              key={t.name}
              href={t.href}
              target="_blank"
              rel="noreferrer"
              className="flex flex-col items-center gap-1.5 py-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
              data-testid={`badge-share-${t.name.toLowerCase()}`}
            >
              <t.icon className={`w-5 h-5 ${t.color}`} />
              <span className="text-[10px] text-muted-foreground">{t.name}</span>
            </a>
          ))}
          <button
            type="button"
            onClick={copyText}
            className="flex flex-col items-center gap-1.5 py-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
            data-testid="badge-share-copy"
          >
            <Copy className="w-5 h-5 text-amber-400" />
            <span className="text-[10px] text-muted-foreground">Copy</span>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
